"use client"

import { ExternalLink } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslations } from "next-intl"


interface WhatsappConnectNoticeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * What connecting a number already used in the WhatsApp Business app costs the
 * customer (Meta's "coexistence" mode). Informational only — opened from a link
 * on the connect tile; two of these are irreversible enough that finding out
 * afterwards is a support ticket.
 */
export function WhatsappConnectNoticeDialog({
  open,
  onOpenChange,
}: WhatsappConnectNoticeDialogProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWhatsappConnectNoticeDialog")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><span key={tUi("connectingAWhatsAppBusinessAppNumber")}>{tUi("connectingAWhatsAppBusinessAppNumber")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("youCanConnectABrandNewNumberOr")}>{tUi("youCanConnectABrandNewNumberOr")}</span></DialogDescription>
        </DialogHeader>

        {/* Scoped: none of this applies to a brand-new Cloud API number, and the
            same tile serves both intents. */}
        <p className="text-sm font-medium text-gray-900">
          <span key={tUi("ifYouConnectANumberYouAlreadyUse")}>{tUi("ifYouConnectANumberYouAlreadyUse")}</span></p>
        <ul className="space-y-3 text-sm text-gray-600">
          <li>
            <span className="font-medium text-gray-900"><span key={tUi("linkedDevicesAreUnlinkedOnce")}>{tUi("linkedDevicesAreUnlinkedOnce")}</span></span>{" "}
            <span><span key={tUi("allCompanionAppsAreDisconnectedDuringSetupAnd")}>{tUi("allCompanionAppsAreDisconnectedDuringSetupAnd")}</span></span>{" "}
            <span className="font-medium"><span key={tUi("whatsAppForWindows")}>{tUi("whatsAppForWindows")}</span></span> <span><span key={tUi("and")}>{tUi("and")}</span></span>{" "}
            <span className="font-medium"><span key={tUi("whatsAppForWearOS")}>{tUi("whatsAppForWearOS")}</span></span> <span><span key={tUi("areNotSupportedAndCannotBeReLinked")}>{tUi("areNotSupportedAndCannotBeReLinked")}</span></span>
          </li>
          <li>
            <span className="font-medium text-gray-900"><span key={tUi("keepTheBusinessAppActive")}>{tUi("keepTheBusinessAppActive")}</span></span> <span><span key={tUi("ifThePhoneStaysInactiveForAbout14")}>{tUi("ifThePhoneStaysInactiveForAbout14")}</span></span>
          </li>
          <li>
            <span className="font-medium text-gray-900"><span key={tUi("someAppFeaturesAreTurnedOff")}>{tUi("someAppFeaturesAreTurnedOff")}</span></span>{" "}
            <span><span key={tUi("disappearingViewOnceAndLiveLocationMessagesAre")}>{tUi("disappearingViewOnceAndLiveLocationMessagesAre")}</span></span>
          </li>
          <li>
            <span className="font-medium text-gray-900"><span key={tUi("messagingSpeedIsCapped")}>{tUi("messagingSpeedIsCapped")}</span></span> <span><span key={tUi("theNumberSThroughputIsFixedAt20")}>{tUi("theNumberSThroughputIsFixedAt20")}</span></span>
          </li>
        </ul>

        <div className="space-y-1 text-sm text-gray-600">
          <p className="font-medium text-gray-900"><span key={tUi("learnMoreInTheOfficialDocumentation")}>{tUi("learnMoreInTheOfficialDocumentation")}</span></p>
          <a
            href="https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-blue-600 hover:underline"
          >
            <span><span key={tUi("metaOnboardingWhatsAppBusinessAppUsers")}>{tUi("metaOnboardingWhatsAppBusinessAppUsers")}</span></span>
            <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href="https://faq.whatsapp.com/647349420360876"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-blue-600 hover:underline"
          >
            <span><span key={tUi("whatsAppHelpCenterLinkedDevicesOnTheBusiness")}>{tUi("whatsAppHelpCenterLinkedDevicesOnTheBusiness")}</span></span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}><span key={tUi("gotIt")}>{tUi("gotIt")}</span></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
